import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldWarning, ArrowsClockwise, Star, Robot, UserCircle, Clock, ChatCircleText } from "@phosphor-icons/react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StarRow({ rating }) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  const tone =
    rating >= 4 ? "text-emerald-600" :
    rating >= 3 ? "text-amber-600" :
    "text-rose-600";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`rating-${rating}`}>
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={16}
            weight={i < n ? "fill" : "regular"}
            className={i < n ? tone : "text-stone-300"}
          />
        ))}
      </div>
      <span className={`text-sm font-bold ${tone}`}>{Number(rating).toFixed(1)}</span>
    </div>
  );
}

export default function AdminRecentRatingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/recent-ratings", { params: { limit } });
      setRows(data.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load recent ratings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_super_admin) load(); }, [user, limit]);

  if (authLoading) {
    return (
      <PageShell><Header />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center text-muted">Loading…</div>
        <Footer />
      </PageShell>
    );
  }
  if (!user || !user.is_super_admin) {
    return (
      <PageShell><Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="admin-recent-ratings-denied">
          <ShieldWarning size={36} weight="duotone" className="mx-auto text-rose-600" />
          <h1 className="font-serif-display text-3xl mt-4 font-light text-[#3F2D1B]">Super-admin only</h1>
          <p className="text-muted mt-3 font-light max-w-md mx-auto leading-relaxed">
            This page shows recent chat ratings and is restricted to AstroKuber super-admins.
          </p>
          <Link to="/" className="inline-block mt-7 px-6 py-2.5 rounded-full bg-[#3F2D1B] text-amber-50 text-sm font-bold tracking-wider">
            Back to home
          </Link>
        </div>
        <Footer />
      </PageShell>
    );
  }

  const avg = rows.length
    ? (rows.reduce((s, r) => s + (Number(r.rating) || 0), 0) / rows.length).toFixed(2)
    : "—";
  const lowCount = rows.filter((r) => Number(r.rating) <= 2).length;

  return (
    <PageShell><Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-recent-ratings-page">
        <header className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#7C2D12]">Super-admin · Quality pulse</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 font-light leading-tight text-[#3F2D1B]">
              Recent chat ratings <span className="text-muted text-base font-light">· last {limit}</span>
            </h1>
            <p className="text-muted mt-3 font-light text-sm max-w-2xl">
              The most recent {limit} chat reviews — each shows the rating, the astrologer the seeker
              picked, the chat duration, and the customer who left it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="bg-white border border-amber-300 rounded-full text-xs font-bold tracking-wider px-3 py-2 text-[#3F2D1B]"
              data-testid="admin-recent-ratings-limit"
            >
              {[10, 20, 30, 50].map((n) => <option key={n} value={n}>LAST {n}</option>)}
            </select>
            <button
              type="button"
              onClick={load}
              data-testid="admin-recent-ratings-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300 text-[#3F2D1B] text-xs font-bold tracking-wider hover:bg-amber-50"
            >
              <ArrowsClockwise size={14} weight="bold" /> REFRESH
            </button>
          </div>
        </header>

        {/* Summary strip */}
        <section className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-recent-ratings-summary">
          <div className="rounded-2xl border border-amber-200 bg-white p-4">
            <div className="label-eyebrow text-muted">Reviews shown</div>
            <div className="font-serif-display text-3xl mt-1 text-[#3F2D1B]">{rows.length}</div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-white p-4">
            <div className="label-eyebrow text-muted">Avg rating</div>
            <div className="font-serif-display text-3xl mt-1 text-[#3F2D1B]">{avg}</div>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
            <div className="label-eyebrow text-rose-700">Low (≤2)</div>
            <div className="font-serif-display text-3xl mt-1 text-rose-700">{lowCount}</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
            <div className="label-eyebrow text-emerald-700">High (=5)</div>
            <div className="font-serif-display text-3xl mt-1 text-emerald-700">{rows.filter((r) => Number(r.rating) === 5).length}</div>
          </div>
        </section>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="admin-recent-ratings-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-14 text-center text-muted">Loading recent ratings…</div>
        ) : rows.length === 0 ? (
          <div className="mt-12 rounded-2xl p-10 bg-amber-50 border border-amber-200 text-center" data-testid="admin-recent-ratings-empty">
            <p className="font-serif-display text-2xl font-light text-[#3F2D1B]">No ratings yet.</p>
            <p className="text-muted mt-2 text-sm font-light">Reviews will appear here as seekers rate their chats.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {rows.map((row, idx) => {
              const r = Number(row.rating);
              const tone =
                r >= 4 ? "border-emerald-200 bg-emerald-50/30" :
                r >= 3 ? "border-amber-200 bg-amber-50/30" :
                "border-rose-300 bg-rose-50/40";
              return (
                <article
                  key={row.chat_id}
                  className={`rounded-2xl border overflow-hidden ${tone}`}
                  data-testid={`admin-rating-row-${row.chat_id}`}
                >
                  <div className="p-4 sm:p-5 grid sm:grid-cols-[1fr_auto] gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">
                          #{idx + 1}
                        </span>
                        <StarRow rating={row.rating} />
                        <span className="text-xs text-muted inline-flex items-center gap-1">
                          <Clock size={12} weight="duotone" /> {fmtDateTime(row.created_at)}
                        </span>
                        {row.duration_minutes != null && (
                          <span className="text-xs text-muted inline-flex items-center gap-1">
                            <ChatCircleText size={12} weight="duotone" /> {row.duration_minutes} min chat
                          </span>
                        )}
                      </div>
                      {/* astrologer + customer */}
                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {row.astrologer.avatar ? (
                            <img
                              src={row.astrologer.avatar}
                              alt={row.astrologer.name}
                              className="w-10 h-10 rounded-full object-cover border border-amber-200 shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <UserCircle size={40} weight="duotone" className="text-stone-400 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-[10px] text-muted tracking-wider uppercase">Astrologer</div>
                            <div className="text-sm font-bold text-[#3F2D1B] truncate flex items-center gap-1">
                              {row.astrologer.name}
                              {row.astrologer.is_ai && (
                                <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-950 inline-flex items-center gap-0.5">
                                  <Robot size={9} weight="bold" /> AI
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted truncate">
                              {row.astrologer.specialization}
                              {row.astrologer.experience_years ? ` · ${row.astrologer.experience_years} yrs` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2.5 min-w-0">
                          {row.customer.picture ? (
                            <img
                              src={row.customer.picture}
                              alt={row.customer.name}
                              className="w-10 h-10 rounded-full object-cover border border-stone-200 shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <UserCircle size={40} weight="duotone" className="text-stone-400 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-[10px] text-muted tracking-wider uppercase">Customer</div>
                            <div className="text-sm font-bold text-[#3F2D1B] truncate">{row.customer.name}</div>
                            {row.customer.email && (
                              <a href={`mailto:${row.customer.email}`} className="text-[11px] text-muted truncate underline">
                                {row.customer.email}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      {row.comment && (
                        <blockquote className="mt-3 px-3 py-2 rounded-md bg-white/70 border border-stone-200 text-sm text-foreground/85 italic leading-relaxed">
                          "{row.comment}"
                        </blockquote>
                      )}
                      <div className="mt-2 text-[10px] text-muted font-mono">chat_id: {row.chat_id}</div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </PageShell>
  );
}
